import type { BadgeTone } from "@altek/ui/components/ui/Badge";
import { formatDecimalString, formatMoney } from "@altek/ui/lib/format/number";

/**
 * Filonun sabitleri → Türkçe ad ve rozet tonu. Sunucu kodu taşıyor (`IN_POOL`), ekran cümleyi.
 *
 * DURUM RENGİ: kitin kuralı «durum rengine yalnız `StatusBadge` karar verir»; ama `StatusBadge`
 * filo alanlarını henüz tanımıyor (kit talebi K1, `docs/plan/FILO_KIT_TALEPLERI.md`). O gelene kadar
 * tonlar BURADA, tek yerde — kit gelince bu tablolar silinip `StatusBadge domain="fleet.*"` olur.
 */

type Labelled = Readonly<Record<string, string>>;

export const VEHICLE_STATE_LABELS: Labelled = {
  IN_POOL: "Müsait",
  ASSIGNED: "Zimmette",
  IN_SERVICE: "Serviste",
  DAMAGED: "Hasarlı",
  INACTIVE: "Pasif",
  SOLD: "Satıldı",
};

export const VEHICLE_STATE_TONE: Readonly<Record<string, BadgeTone>> = {
  IN_POOL: "success",
  ASSIGNED: "info",
  IN_SERVICE: "warning",
  DAMAGED: "danger",
  INACTIVE: "neutral",
  SOLD: "neutral",
};

export const OWNERSHIP_LABELS: Labelled = { OWNED: "Şirket malı", RENTED: "Kiralık", LEASED: "Leasing" };
export const FUEL_TYPE_LABELS: Labelled = {
  GASOLINE: "Benzin",
  DIESEL: "Dizel",
  LPG: "LPG",
  HYBRID: "Hibrit",
  ELECTRIC: "Elektrik",
  OTHER: "Diğer",
};
export const FUEL_PRODUCT_LABELS: Labelled = {
  GASOLINE: "Benzin",
  DIESEL: "Motorin",
  LPG: "LPG",
  ADBLUE: "AdBlue",
  ELECTRIC: "Elektrik",
  OTHER: "Diğer",
};
export const TRANSMISSION_LABELS: Labelled = { MANUAL: "Manuel", AUTOMATIC: "Otomatik" };

export const IDENTIFIER_KIND_LABELS: Labelled = {
  HGS_TAG: "HGS etiketi",
  OGS_DEVICE: "OGS cihazı",
  UTTS_UNIT: "UTTS taşıt tanıma",
  TELEMATICS_UNIT: "Araç takip cihazı",
  FUEL_CARD: "Yakıt kartı",
};

export const DOCUMENT_TYPE_LABELS: Labelled = {
  TRAFFIC_INSURANCE: "Trafik sigortası",
  COMPREHENSIVE_INSURANCE: "Kasko",
  INSPECTION: "Muayene",
  EXHAUST_EMISSION: "Egzoz emisyon",
  OTHER: "Diğer belge",
};

/**
 * Belgenin geçerliliği — sunucunun DEĞERLERİ (`documentValidity`, `fleet.mapper.ts`): ACTIVE ·
 * EXPIRING · EXPIRED. Burada `VALID` yazıyordu; geçerli belge rozeti ham «ACTIVE · 169 gün»
 * gösteriyordu (10 Ekim 2026, canlı kontrol).
 */
export const VALIDITY_LABELS: Labelled = { ACTIVE: "Geçerli", EXPIRING: "Yaklaşıyor", EXPIRED: "Süresi doldu" };
export const VALIDITY_TONE: Readonly<Record<string, BadgeTone>> = { ACTIVE: "success", EXPIRING: "warning", EXPIRED: "danger" };

export const MAINTENANCE_TYPE_LABELS: Labelled = {
  PERIODIC_MAINTENANCE: "Periyodik bakım",
  OIL_CHANGE: "Yağ değişimi",
  TIRE_CHANGE: "Lastik değişimi",
  BRAKE: "Fren",
  BATTERY: "Akü",
  INSPECTION: "Muayene",
  OTHER: "Diğer",
};
export const PLAN_KIND_LABELS: Labelled = { KM: "Km'ye göre", TIME: "Süreye göre", BOTH: "Km ya da süre" };
export const DUE_STATE_LABELS: Labelled = { OK: "Vakti var", SOON: "Yaklaşıyor", DUE: "Vakti geldi", OVERDUE: "Gecikti" };
export const DUE_STATE_TONE: Readonly<Record<string, BadgeTone>> = { OK: "success", SOON: "warning", DUE: "warning", OVERDUE: "danger" };

export const SERVICE_TYPE_LABELS: Labelled = {
  PERIODIC_MAINTENANCE: "Periyodik bakım",
  REPAIR: "Onarım",
  TIRE: "Lastik",
  BODY_PAINT: "Kaporta-boya",
  GLASS: "Cam",
  ELECTRICAL: "Elektrik",
  INSPECTION: "Muayene",
  OTHER: "Diğer",
};
export const SERVICE_STATUS_LABELS: Labelled = { PLANNED: "Planlandı", IN_SERVICE: "Serviste", COMPLETED: "Tamamlandı", CANCELLED: "İptal" };
export const SERVICE_STATUS_TONE: Readonly<Record<string, BadgeTone>> = { PLANNED: "info", IN_SERVICE: "warning", COMPLETED: "success", CANCELLED: "neutral" };

export const DAMAGE_TYPE_LABELS: Labelled = {
  SCRATCH: "Çizik",
  DENT: "Göçük",
  CRACK: "Çatlak",
  BROKEN_GLASS: "Kırık cam",
  TIRE: "Lastik",
  INTERIOR: "İç döşeme",
  MECHANICAL: "Mekanik",
  ELECTRICAL: "Elektrik",
  ACCIDENT: "Kaza",
  OTHER: "Diğer",
};
export const SEVERITY_LABELS: Labelled = { MINOR: "Küçük", MODERATE: "Orta", MAJOR: "Büyük", CRITICAL: "Kritik" };
export const SEVERITY_TONE: Readonly<Record<string, BadgeTone>> = { MINOR: "neutral", MODERATE: "warning", MAJOR: "danger", CRITICAL: "danger" };
/** «Teslimde vardı»: araç kişiye verilirken zaten olan hasar — kişiye yazılmaz. Yalnız «Teslimde» iken satırdaki kişi adı «bu kişi yaptı» diye okunuyordu. */
export const DETECTED_DURING_LABELS: Labelled = { HANDOVER: "Teslimde vardı", RETURN: "İadede", USAGE: "Kullanımda", SERVICE: "Serviste" };
export const FAULT_LABELS: Labelled = { NOT_AT_FAULT: "Kusursuz", PARTIAL: "Kısmi kusurlu", AT_FAULT: "Kusurlu", UNKNOWN: "Belirsiz" };
export const DAMAGE_STATUS_LABELS: Labelled = { OPEN: "Açık", ASSESSMENT: "Ekspertizde", IN_REPAIR: "Onarımda", CLOSED: "Kapandı" };
export const DAMAGE_STATUS_TONE: Readonly<Record<string, BadgeTone>> = { OPEN: "danger", ASSESSMENT: "warning", IN_REPAIR: "warning", CLOSED: "success" };
/** Hasar akışında bir sonraki adım(lar) — sunucunun `DAMAGE_FLOW`'uyla aynı. */
export const DAMAGE_NEXT: Readonly<Record<string, readonly string[]>> = {
  OPEN: ["ASSESSMENT", "IN_REPAIR", "CLOSED"],
  ASSESSMENT: ["IN_REPAIR", "CLOSED"],
  IN_REPAIR: ["CLOSED"],
  CLOSED: [],
};

export const VIOLATION_LABELS: Labelled = {
  SPEED: "Hız",
  RED_LIGHT: "Kırmızı ışık",
  PARKING: "Park",
  SEAT_BELT: "Emniyet kemeri",
  PHONE: "Telefon",
  TOLL_EVASION: "Kaçak geçiş",
  DOCUMENT: "Belge eksiği",
  OTHER: "Diğer",
};
export const FINE_STATUS_LABELS: Labelled = { PENDING: "Ödenmedi", PAID: "Ödendi", DISPUTED: "İtirazda", CANCELLED: "İptal" };
export const FINE_STATUS_TONE: Readonly<Record<string, BadgeTone>> = { PENDING: "warning", PAID: "success", DISPUTED: "info", CANCELLED: "neutral" };
export const RECOURSE_LABELS: Labelled = { NONE: "Rücu yok", TO_RECOVER: "Rücu edilecek", RECOVERED: "Rücu edildi", WAIVED: "Vazgeçildi" };
export const MATCH_LABELS: Labelled = { MATCHED: "Eşleşti", SUGGESTED: "Öneri", UNMATCHED: "Eşleşmedi" };
export const MATCH_TONE: Readonly<Record<string, BadgeTone>> = { MATCHED: "success", SUGGESTED: "info", UNMATCHED: "warning" };

export const TOLL_BANK_LABELS: Labelled = { VAKIFBANK: "VakıfBank", AKBANK: "Akbank", YAPIKREDI: "Yapı Kredi", PTT: "PTT", OTHER: "Diğer" };
export const PAYMENT_METHOD_LABELS: Labelled = {
  FUEL_CARD: "Taşıt/yakıt kartı",
  COMPANY_CARD: "Şirket kartı",
  CASH: "Nakit",
  EMPLOYEE: "Çalışan ödedi",
  BANK_TRANSFER: "Havale",
  AUTO_DEBIT: "Otomatik ödeme",
};

/** «Taksitli / senetli alım» (31 Ekim 2026): satıcıya senetle vadeli alım da bu tür — ayrı bir tür değil. */
export const FINANCING_TYPE_LABELS: Labelled = { LOAN: "Taşıt kredisi", INSTALLMENT_PURCHASE: "Taksitli / senetli alım", LEASE: "Leasing" };
export const FINANCING_STATUS_LABELS: Labelled = { ACTIVE: "Sürüyor", CLOSED: "Bitti", SETTLED_EARLY: "Erken kapatıldı", CANCELLED: "İptal" };
export const EXPENSE_CATEGORY_LABELS: Labelled = {
  FUEL: "Yakıt",
  TOLL: "Köprü/otoyol",
  FINE: "Ceza",
  MAINTENANCE: "Bakım",
  REPAIR: "Onarım",
  INSURANCE: "Sigorta/kasko",
  INSPECTION: "Muayene",
  TAX: "MTV/vergi",
  TIRE: "Lastik",
  WASH: "Yıkama",
  PARKING: "Otopark",
  INTEREST: "Faiz",
  PRINCIPAL: "Anapara",
  LEASE: "Kira/leasing",
  OTHER: "Diğer",
};

export const IMPORT_KIND_LABELS: Labelled = {
  TOLL: "HGS geçişleri",
  FUEL: "Yakıt (Shell TTS / UTTS)",
  FINE: "Trafik cezaları",
  ODOMETER: "Kilometre",
  // 31 Ekim 2026 (kullanıcı: «UTTS, Mobiliz canlı konum takip, HGS ve ceza ve vergi … bunları çekeceğiz»).
  TAX: "MTV / vergi borcu",
  POSITION: "Konum ve güzergâh (araç takip)",
};
/**
 * Bir kaydın nereden geldiği (31 Ekim 2026: entegrasyon verisi araç ve zimmet ekranlarında kaynağıyla).
 * `FILE` dökümden içe aktarılmış, `API` dış sistemden itilmiş ya da çekilmiş, `MANUAL` elle girilmiş.
 */
export const ROW_SOURCE_LABELS: Labelled = { MANUAL: "Elle", FILE: "Döküm", IMPORT: "Döküm", API: "Dış sistem", MOBILIZ: "Mobiliz" };
/** filo-duzeltme 20 Ekim 2026: muayene sonucu, MTV dönemi ve durumu, maliyet kırılımı. */
export const INSPECTION_RESULT_LABELS: Labelled = { PASSED: "Geçti", FAILED: "Kaldı", RETEST: "Tekrar muayene" };
export const TAX_PERIOD_LABELS: Labelled = { "1": "Ocak taksiti", "2": "Temmuz taksiti" };
export const TAX_STATUS_LABELS: Labelled = { PENDING: "Ödenmedi", PAID: "Ödendi" };
export const COST_GROUP_LABELS: Labelled = { vehicle: "Araç", month: "Ay", category: "Kalem", employee: "Sürücü", costCenter: "Masraf merkezi" };
export const PAID_BY_LABELS: Labelled = { COMPANY: "Şirket", EMPLOYEE: "Çalışan (kendisi ödedi)" };
export const IMPORT_STATUS_LABELS: Labelled = { QUEUED: "Sırada", PROCESSING: "İşleniyor", DONE: "Tamamlandı", FAILED: "Başarısız" };

export const RECOGNITION_FIELD_LABELS: Labelled = {
  plate: "Plaka",
  brand: "Marka",
  model: "Model",
  color: "Renk",
  bodyType: "Kasa tipi",
  vin: "Şasi no",
  engineNo: "Motor no",
  registrationSerialNo: "Belge seri no",
  modelYear: "Model yılı",
  fuelType: "Yakıt",
  firstRegistrationDate: "İlk tescil",
  registrationDate: "Tescil tarihi",
  engineVolumeCc: "Silindir hacmi (cm³)",
  enginePowerKw: "Motor gücü (kW)",
  seatCount: "Koltuk sayısı",
  vehicleClass: "Araç sınıfı",
};

/** Sabitin adı, yoksa kodun kendisi — bilinmeyen kod boş hücre değil, görünür bir değer. */
export function labelOf(table: Labelled, code: string | null | undefined): string {
  if (!code) return "—";
  return table[code] ?? code;
}

export function toneOf(table: Readonly<Record<string, BadgeTone>>, code: string | null | undefined): BadgeTone {
  return (code ? table[code] : undefined) ?? "neutral";
}

/** Para: ondalık DİZE, hesap yok. */
export function money(amount: string | null | undefined, currency?: string | null): string {
  return formatMoney(amount ?? null, currency ?? "TRY");
}

/**
 * Tutarın birimi, kitin `KpiCard.unit` yuvası için (UI/UX denetimi 31 Ekim 2026, F-A5): «168.750,00 TL»
 * değerin içinde dar kutudan taşıyordu; rakam değerde, birim yanında küçük. `formatMoney`'in adıyla aynı.
 */
export function currencyUnit(currency?: string | null): string {
  return currency === "TRY" || !currency ? "TL" : currency;
}

/** Km, binlik ayraçlı: «45.200 km». */
export function km(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${formatDecimalString(String(value))} km`;
}

/** Plakanın okunur hâli: «34ABC123» → «34 ABC 123». Sunucu çoğu yerde `plateDisplay` da veriyor. */
export function plateDisplay(plate: string | null | undefined): string {
  if (!plate) return "—";
  const match = /^(\d{2})([A-ZÇĞİÖŞÜ]+)(\d+)$/.exec(plate);
  return match ? `${match[1]} ${match[2]} ${match[3]}` : plate;
}
